"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
exports.useCustomerDetails = useCustomerDetails;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = require("react");
var _shopperData = _interopRequireDefault(require("./shopper-data"));
var _user = _interopRequireDefault(require("./user"));
var _cookies = _interopRequireDefault(require("./cookies"));
var _employee = _interopRequireDefault(require("./employee"));
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { (0, _defineProperty2.default)(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
var constants = require('./constants');
const status = ['partial', 'full', 'manager'];

/**
 * Customer details
 *
 * @Constructor Customer
 * @param {Object} options Optional configuration.
 * @api public
 */
class Customer extends _user.default {
  /**
   * Update data from the idp cookie
   * @param {String} loggedOutCookieType The cookie type to parse
   * @returns {Customer} fluent interface
   * @api private
   */
  updateFromCookie() {
    let loggedOutCookieType = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : '';
    this.cookies.updateIdp(loggedOutCookieType);
    const auth = this.cookies.getIdpProperty('auth');
    this.set(this.getIdpProperties(this.cookies.getIdpProperties.bind(this.cookies), auth));
    if (!this.shopperId) {
      // Try getting it from the ShopperId cookie instead
      const encryptedShopperId = this.cookies.getEncryptedShopperId(this.options.env, this.options.privateLabelId);
      if (encryptedShopperId) {
        this.set(_objectSpread(_objectSpread({}, this.getAll()), {}, {
          shopperId: encryptedShopperId
        }));
      }
    }
    return this;
  }

  /**
   * Get properties from idp information
   *
   * @param {Function} getIdpPropertiesFn Function to call to get properties from idp
   * @param {String} auth Type of authentication (e.g. e2s, s2s, e2s2s, none of the above)
   * @returns {Object} Extracted properties
   * @package
   */
  getIdpProperties(getIdpPropertiesFn, auth) {
    return getIdpPropertiesFn(['disp', 'firstname', 'lastname', 'info_shopperId', 'plid', 'pcx', 'username'].map(property => [auth, property]).concat('jti'), {
      info_shopperId: 'shopperId',
      disp: 'displayName'
    });
  }

  /**
   * Fetch all user details, unpack and store locally.
   * TODO: JSONP should not be enforced for details but CORS headers are missing.
   *
   * @param {Object} url Request targets.
   * @param {Function} done Completion callback
   * @api public
   */
  fetch(url, done) {
    this.cookies.updateIdp();
    this.fetchInternal(url, done, data => {
      let idp;
      try {
        idp = JSON.parse(data.info);
      } catch (e) {
        return {};
      }
      return this.getIdpProperties(_cookies.default.getRealmPropertiesFromObject.bind(this, idp), idp.auth);
    });
  }

  /**
   * Return loggedIn status of Customer.
   *
   * @returns {Boolean} LoggedIn.
   * @api public
   */
  get loggedIn() {
    const privateLabelId = this.options.privateLabelId;
    if (privateLabelId && this.data.plid && privateLabelId.toString() !== this.data.plid.toString()) {
      return false;
    }
    return !!~status.indexOf(this.get('status')) || !!this.get('loginName');
  }
}

/**
 * Set the customer details.
 *
 * @param {Object} customer cookie info
 * @returns {Object} customer details
 * @public
 */
exports.default = Customer;
function setCustomerDetailsFromCustomer(customer) {
  const proUser = customer.get('proshopper');
  const proshopper = proUser && proUser.status === 1;
  const rewardPoints = proshopper ? proUser.rewardPoints : null;
  return {
    displayName: customer.displayName(true),
    federatedPartner: customer.get('federatedPartner'),
    items: customer.cart,
    isPro: proshopper,
    loggedIn: customer.loggedIn,
    name: customer.displayName(),
    proshopper,
    rewardPoints,
    shopperId: customer.shopperId,
    username: customer.username,
    vip: customer.vip,
    pcx: customer.pcx,
    customer
  };
}

/**
 * Get the (logged-in) customer details.
 *
 * @param {String} url API url, usually GUI API.
 * @param {Object} opt Options
 * @param {String} opt.privateLabelId Private Label id.
 * @param {String} [opt.shouldAuthenticate=true] Skip customer details API call if not needed.
 * @param {Boolean} [opt.initCustomerState=true] Boolean indicating whether customer state should be initialized.
 * @returns {Object} customer details
 * @public
 */

function useCustomerDetails(url) {
  let {
    initCustomerState = true,
    privateLabelId,
    preset,
    shouldAuthenticate = true
  } = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  const [customerDetails, setCustomerDetails] = (0, _react.useState)({});
  const customerDetailsRef = (0, _react.useRef)(customerDetails);
  (0, _react.useEffect)(() => {
    customerDetailsRef.current = customerDetails;
  }, [customerDetails]);
  (0, _react.useEffect)(() => {
    if (!initCustomerState || !url) {
      return;
    }

    // This function will be called whenever the tab gets focus
    const handleFocus = () => {
      if (customerDetailsRef.current.customer) {
        // if there is a customer present, update it from the cookie
        customerDetailsRef.current.customer.updateFromCookie();
        setCustomerDetails(setCustomerDetailsFromCustomer(customerDetailsRef.current.customer));
      } else {
        // if there is no customer present, try creating a new one from the cookie
        let customer;

        // Override customer if internal mode
        if (preset === 'internal-header') {
          customer = new _employee.default({
            privateLabelId
          });
          customer.updateFromCookie();
        } else {
          customer = new Customer({
            privateLabelId
          }).updateFromCookie();
        }
        setCustomerDetails({
          customer
        });
      }
    };

    // Add the event listener when the component mounts
    window.addEventListener('focus', handleFocus);

    // Remove the event listener when the component unmounts
    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [initCustomerState, preset, privateLabelId, url]);
  (0, _react.useEffect)(() => {
    if (!initCustomerState || !url) {
      return;
    }

    // Start with data from the existing cookie

    let customer;

    // Override customer if internal mode
    if (preset === 'internal-header') {
      customer = new _employee.default({
        privateLabelId
      });
      customer.updateFromCookie();
    } else {
      customer = new Customer({
        privateLabelId
      }).updateFromCookie();
    }
    setCustomerDetails({
      customer
    });
    if (!shouldAuthenticate) {
      _shopperData.default.configureOnly({
        shopperId: customer.shopperId,
        privateLabelId,
        url
      });
      return;
    }

    // Next, try to fetch fresh data from the server
    customer.fetch({
      gui: url
    }, error => {
      if (error) {
        // try and get customer data from logged out cookie
        const loggedoutCustomer = new Customer().updateFromCookie(constants.LOGGED_OUT_INFO_COOKIE);
        setCustomerDetails({
          loggedoutCustomer
        });
        loggedoutCustomer.fetch({
          gui: url
        }, err => {
          if (err) {
            // The Custoshoppermer Cookie, cust_idp, is a no-auth cookie that is
            // used by operations at the company where no auth is required to interact
            // with a flow. The common case is in eComm (think: adding items to your
            // cart).

            const loggedoutCheckoutCustomer = new Customer().updateFromCookie(constants.CUSTO_SHOPPER_MER_COOKIE);
            setCustomerDetails({
              loggedoutCheckoutCustomer
            });
            loggedoutCheckoutCustomer.fetch({
              gui: url
            }, e => {
              if (e) {
                // We still have some data from the cookie, so just fall back to that.
                // We use the presence of a shopper ID as a weak proxy to login state.
                // This won't actually unlock any real actions -- just tells the header to act
                // like the user is logged in. If they are not really logged in, they'll get
                // sent to SSO when they try to do anything that requires login.

                if (loggedoutCheckoutCustomer.shopperId) {
                  return setCustomerDetails({
                    displayName: loggedoutCheckoutCustomer.displayName(true),
                    federatedPartner: loggedoutCheckoutCustomer.get('federatedPartner'),
                    name: loggedoutCheckoutCustomer.displayName(),
                    shopperId: loggedoutCheckoutCustomer.shopperId,
                    loggedIn: true,
                    loggedoutCheckoutCustomer
                  });
                }
              }
              setCustomerDetails(setCustomerDetailsFromCustomer(loggedoutCheckoutCustomer));
            });
          }
          setCustomerDetails(setCustomerDetailsFromCustomer(loggedoutCustomer));
        });
      }
      if (preset === 'internal-header') {
        // alias username to loginName for internal-header Employee instance
        customer.set(_objectSpread(_objectSpread({}, customer.getAll()), {}, {
          username: customer.get('loginName')
        }));
      }
      setCustomerDetails(setCustomerDetailsFromCustomer(customer));
    });
  }, [initCustomerState, url, privateLabelId, preset, shouldAuthenticate]);
  return customerDetails;
}